import React from "react";
import { Badge, Card, Col, Row, Tooltip } from "antd";
import Supervision from "../../../../../models/Supervision";
import { useLanguage } from "../../../../../context/LanguageContext";

interface CorridorSelectorMapProps {
  corridors: Supervision[];
  selectedCorridorName: string | null;
  onSelect: (corridorName: string | null) => void;
  getCorridorSeverity: (supervision: Supervision) => {
    color: string;
    strokeColor: string;
  };
}

const CorridorSelectorMap: React.FC<CorridorSelectorMapProps> = ({
  corridors,
  selectedCorridorName,
  onSelect,
  getCorridorSeverity,
}) => {
  const { language } = useLanguage();

  const getRackSelectorLabel = (corridorName: string) => {
    const lettersGroup = corridorName.trim().match(/[A-Za-z]+/)?.[0] || "";
    if (lettersGroup.length > 0) {
      return lettersGroup.slice(0, 2).toUpperCase();
    }
    return corridorName.trim().slice(0, 2).toUpperCase();
  };

  return (
    <Row gutter={[12, 12]}>
      <Col span={24}>
        <Card
          size="small"
          title={language === "es" ? "Selector de Racks" : "Rack Selector"}
          bodyStyle={{ padding: "10px" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))",
              gap: "8px",
              alignContent: "start",
              maxHeight: "240px",
              overflowY: "auto",
              paddingRight: "4px",
            }}
          >
            <button
              type="button"
              onClick={() => onSelect(null)}
              style={{
                border: selectedCorridorName === null ? "2px solid #1677ff" : "1px solid #d9d9d9",
                borderRadius: "8px",
                background: selectedCorridorName === null ? "#e6f4ff" : "#fff",
                padding: "4px 6px",
                textAlign: "center",
                cursor: "pointer",
                minHeight: "52px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: "12px", lineHeight: 1 }}>
                {language === "es" ? "TODO" : "ALL"}
              </div>
              <div style={{ minHeight: "8px", display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "100%",
                    height: "6px",
                    borderRadius: "999px",
                    backgroundColor: "#1677ff",
                  }}
                />
              </div>
            </button>
            {corridors.map((corridor) => {
              const severity = getCorridorSeverity(corridor);
              const isSelected = corridor.corridor_name === selectedCorridorName;
              const isUnexplored = corridor.reading_percentage <= 0.1;

              return (
                <Tooltip
                  key={corridor.corridor_idx}
                  title={
                    language === "es"
                      ? `Rack ${corridor.corridor_name}`
                      : `Corridor ${corridor.corridor_name}`
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      onSelect(
                        isSelected ? null : corridor.corridor_name
                      )
                    }
                    style={{
                      border: isSelected ? "2px solid #1677ff" : "1px solid #d9d9d9",
                      borderRadius: "8px",
                      background: isSelected ? "#e6f4ff" : "#fff",
                      padding: "4px 6px",
                      textAlign: "center",
                      cursor: "pointer",
                      minHeight: "52px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: "14px", lineHeight: 1 }}>
                      {getRackSelectorLabel(corridor.corridor_name)}
                    </div>
                    <div style={{ minHeight: "8px", display: "flex", alignItems: "center" }}>
                      {isUnexplored ? (
                        <Badge color="#fadb14" />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "6px",
                            borderRadius: "999px",
                            backgroundColor: severity.strokeColor,
                          }}
                        />
                      )}
                    </div>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default CorridorSelectorMap;